import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Play, Pause } from "lucide-react";
import { useRef, useState, useEffect, useCallback } from "react";

const tracks = [
  {
    title: "«Антоха — король мороженого»",
    genre: "Rap • Полная версия",
    file: "demo.mp3",
  },
  {
    title: "«Виктория и Питер»",
    genre: "Rap • Полная версия",
    file: "demo2.mp3",
  },
];

const baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/demo-audio`;

interface TrackPlayerProps {
  track: typeof tracks[0];
  isActive: boolean;
  isPlaying: boolean;
  onTogglePlay: () => void;
  progress: number;
  duration: number;
  onSeek: (pct: number) => void;
}

const TrackPlayer = ({ track, isActive, isPlaying, onTogglePlay, progress, duration, onSeek }: TrackPlayerProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  const formatTime = (s: number) => {
    if (!s || !isFinite(s)) return "0:00";
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatRemaining = (prog: number, dur: number) => {
    if (!dur || !isFinite(dur)) return "—:——";
    const rem = dur - prog;
    return `-${formatTime(rem)}`;
  };

  const calcPct = (clientX: number) => {
    if (!barRef.current) return 0;
    const rect = barRef.current.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive) return;
    setIsDragging(true);
    onSeek(calcPct(e.clientX));
  };

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => onSeek(calcPct(e.clientX));
    const onUp = () => setIsDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging, onSeek]);

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isActive) return;
    onSeek(calcPct(e.touches[0].clientX));
  };

  const pct = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div
      className={`p-4 sm:p-5 rounded-xl border transition-all ${
        isActive
          ? "bg-card border-primary/30 shadow-soft"
          : "bg-card/50 border-border hover:border-primary/20"
      }`}
    >
      <div className="flex items-center gap-4">
        <button
          onClick={onTogglePlay}
          aria-label={isActive && isPlaying ? `Пауза: ${track.title}` : `Играть: ${track.title}`}
          className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity ${
            isActive && isPlaying
              ? "bg-primary text-primary-foreground"
              : "bg-primary/80 text-primary-foreground hover:bg-primary"
          }`}
        >
          {isActive && isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </button>

        <div className="flex-1 min-w-0 text-left">
          <p className="font-display text-sm font-semibold text-foreground truncate">
            🎵 {track.title}
          </p>
          <p className="text-muted-foreground text-xs">{track.genre}</p>

          <div
            ref={barRef}
            className={`group w-full h-2.5 bg-muted rounded-full mt-2 relative ${isActive ? "cursor-pointer" : "cursor-default"}`}
            onMouseDown={handleMouseDown}
            onTouchStart={(e) => { if (isActive) onSeek(calcPct(e.touches[0].clientX)); }}
            onTouchMove={handleTouchMove}
          >
            <div
              className="h-full bg-primary rounded-full transition-[width] duration-100"
              style={{ width: `${pct}%` }}
            />
            {isActive && duration > 0 && (
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-primary border-2 border-background shadow-sm transition-[left] duration-100"
                style={{ left: `calc(${pct}% - 7px)` }}
              />
            )}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-muted-foreground">{isActive ? formatTime(progress) : "0:00"}</span>
            <span className="text-xs text-muted-foreground">{isActive ? formatRemaining(progress, duration) : "—:——"}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const AudioDemoSection = () => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [activeTrack, setActiveTrack] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioUrl = activeTrack !== null ? `${baseUrl}/${tracks[activeTrack].file}` : "";

  const handleTogglePlay = useCallback((index: number) => {
    if (activeTrack === index) {
      // Same track — toggle play/pause
      if (!audioRef.current) return;
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    } else {
      // Different track — switch
      if (audioRef.current) audioRef.current.pause();
      setProgress(0);
      setDuration(0);
      setIsPlaying(true);
      setActiveTrack(index);
    }
  }, [activeTrack, isPlaying]);

  // Auto-play when track changes
  useEffect(() => {
    if (activeTrack === null) return;
    const audio = audioRef.current;
    if (!audio) return;
    audio.load();
    audio.play().catch(() => setIsPlaying(false));
  }, [activeTrack]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => setProgress(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => {
      setIsPlaying(false);
      setProgress(0);
    };

    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const handleSeek = (pct: number) => {
    if (!audioRef.current || !duration) return;
    audioRef.current.currentTime = pct * duration;
    setProgress(pct * duration);
  };

  return (
    <section className="py-12 relative" id="demo">
      <div className="container px-4 mx-auto">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-3 text-foreground">
            Послушайте <span className="text-gradient-primary">примеры</span>
          </h2>
          <p className="text-muted-foreground text-base mb-8">
            Вот как звучат персональные хиты от Хит Шеф
          </p>

          <audio ref={audioRef} src={audioUrl} preload="metadata" />

          <div className="flex flex-col gap-3 mb-6">
            {tracks.map((track, i) => (
              <TrackPlayer
                key={i}
                track={track}
                isActive={activeTrack === i}
                isPlaying={activeTrack === i && isPlaying}
                onTogglePlay={() => handleTogglePlay(i)}
                progress={activeTrack === i ? progress : 0}
                duration={activeTrack === i ? duration : 0}
                onSeek={handleSeek}
              />
            ))}
          </div>

          <Button className="shadow-soft" asChild>
            <Link to="/create">
              🔥 Создать свой хит
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AudioDemoSection;
